// ======================================================================
// 📁 File: src/modules/ppe/pages/engineering/routing/RoutingListPage.jsx
// ======================================================================

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axiosClient from "../../../../../api/axiosClient";

import RoutingKpiBar from "./RoutingKpiBar";

export default function RoutingListPage() {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [productFamilies, setProductFamilies] = useState([]);
  const [pfMap, setPfMap] = useState({});

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ALL");
  const [type, setType] = useState("ALL");
  const [productFamilyId, setProductFamilyId] = useState("ALL");
  const [equipments, setEquipments] = useState([]);
  const [equipmentId, setEquipmentId] = useState("ALL");
  // ------------------------------------------------------------
  // Load Product Families
  // ------------------------------------------------------------
  const loadProductFamilies = useCallback(async () => {
    try {
      const res = await axiosClient.get(
        "/api/ppe/masters/product-families"
      );
      const data = res.data?.data || [];
      setProductFamilies(data);

      const map = {};
      data.forEach((pf) => {
        map[pf.id] = pf.name;
      });
      setPfMap(map);
    } catch (err) {
      console.error("Failed to load product families", err);
    }
  }, []);

  useEffect(() => {
    axiosClient
      .get("/api/ppe/masters/equipment")
      .then((res) => {
        setEquipments(res.data?.data || []);
      })
      .catch(() => setEquipments([]));
  }, []);
  // ------------------------------------------------------------
  // Load Routings
  // ------------------------------------------------------------
  const loadRoutings = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (productFamilyId !== "ALL") {
        params.product_family_id = productFamilyId;
      }

      const res = await axiosClient.get(
        "/api/ppe/routing/routings",
        { params }
      );
      setRows(res.data?.data || []);
    } catch (err) {
      console.error("Failed to load routings", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [productFamilyId]);

  useEffect(() => {
    loadProductFamilies();
    loadRoutings();
  }, [loadProductFamilies, loadRoutings]);

  // ------------------------------------------------------------
  // Routing actions
  // ------------------------------------------------------------
  const submitRouting = async (id) => {
    if (!window.confirm("Submit routing for approval?")) return;
    await axiosClient.post(
      `/api/ppe/routing/routings/${id}/submit`
    );
    loadRoutings();
  };

  const approveRouting = async (id) => {
    if (!window.confirm("Approve this routing?")) return;
    await axiosClient.post(
      `/api/ppe/routing/routings/${id}/approve`
    );
    loadRoutings();
  };

  const rejectRouting = async (id) => {
    const reason = window.prompt("Enter rejection reason");
    if (!reason) return;

    await axiosClient.post(
      `/api/ppe/routing/routings/${id}/reject`,
      { reason }
    );
    loadRoutings();
  };

  // ------------------------------------------------------------
  // Filtering (client-side)
  // ------------------------------------------------------------
  const filteredRows = rows.filter((r) => {
    const q = search.toLowerCase();

    const matchesSearch =
      !q ||
      r.routing_code?.toLowerCase().includes(q) ||
      r.routing_name?.toLowerCase().includes(q);

    const matchesStatus =
      status === "ALL" || r.approval_status === status;

    const matchesType =
      type === "ALL" || r.routing_type === type;

    const matchesEquipment =
      equipmentId === "ALL" ||
      String(r.equipment_id) === String(equipmentId);

    return (
      matchesSearch &&
      matchesStatus &&
      matchesType &&
      matchesEquipment
    );
  });
  // ------------------------------------------------------------
  // KPI stats
  // ------------------------------------------------------------
  const stats = {
    total: rows.length,
    draft: rows.filter((r) => r.approval_status === "DRAFT").length,
    approved: rows.filter((r) => r.approval_status === "APPROVED").length,
    estimatedCost: 0,
  };

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------
  return (
    <div>
      {/* ================= HEADER ================= */}
      <div
        style={{
          background:
            "linear-gradient(90deg, #0f766e 0%, #14b8a6 50%, #5eead4 100%)",
          borderRadius: 12,
          padding: "16px 20px",
          color: "#fff",
          marginBottom: 20,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <h4 style={{ margin: 0 }}>Routing Master</h4>
          <div style={{ fontSize: 13, opacity: 0.9 }}>
            Manufacturing routing definitions
          </div>
        </div>

        <button
          className="btn btn-light"
          onClick={() => navigate("/ppe/routing/create")}
        >
          + Create Routing
        </button>
      </div>

      {/* ================= KPI ================= */}
      <RoutingKpiBar stats={stats} />

      {/* ================= FILTERS ================= */}
      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <input
          className="form-control"
          placeholder="Search by code or name"
          style={{ maxWidth: 240 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className="form-select"
          style={{ maxWidth: 200 }}
          value={productFamilyId}
          onChange={(e) => setProductFamilyId(e.target.value)}
        >
          <option value="ALL">All Product Families</option>
          {productFamilies.map((pf) => (
            <option key={pf.id} value={pf.id}>
              {pf.name}
            </option>
          ))}
        </select>
        <select
          className="form-select"
          style={{ maxWidth: 200 }}
          value={equipmentId}
          onChange={(e) => setEquipmentId(e.target.value)}
        >
          <option value="ALL">All Equipment</option>

          {equipments.map((eq) => (
            <option key={eq.id} value={eq.id}>
              {eq.code} – {eq.name}
            </option>
          ))}
        </select>

        <select
          className="form-select"
          style={{ maxWidth: 160 }}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="ALL">All Status</option>
          <option value="DRAFT">Draft</option>
          <option value="SUBMITTED">Submitted</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
          <option value="OBSOLETE">Obsolete</option>
        </select>

        <select
          className="form-select"
          style={{ maxWidth: 160 }}
          value={type}
          onChange={(e) => setType(e.target.value)}
        >
          <option value="ALL">All Types</option>
          <option value="STANDARD">Standard</option>
          <option value="PROJECT">Project</option>
        </select>

        <button
          className="btn btn-primary"
          onClick={loadRoutings}
          disabled={loading}
        >
          Apply
        </button>
      </div>

      {/* ================= TABLE ================= */}
      <div className="card">
        <div className="card-body p-0">
          <table className="table table-bordered mb-0">
            <thead className="table-light">
              <tr>
                <th>Routing Code</th>
                <th>Routing Name</th>
                <th>Product Family</th>
                <th>Equipment</th>   {/* NEW */}
                <th>Version</th>
                <th>Type</th>
                <th>Status</th>
                <th style={{ width: 240 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={7} className="text-center">
                    Loading...
                  </td>
                </tr>
              )}

              {!loading && filteredRows.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-muted">
                    No routings found
                  </td>
                </tr>
              )}

              {filteredRows.map((r) => (
                <tr key={r.id}>
                  <td>{r.routing_code}</td>
                  <td>{r.routing_name}</td>
                  <td>{pfMap[r.product_family_id] || "-"}</td>
                  <td>{r.equipment_name || "-"}</td>   {/* NEW */}
                  <td>v{r.version_no}</td>
                  <td>{r.routing_type}</td>
                  <td>{r.approval_status}</td>
                  <td style={{ display: "flex", gap: 6 }}>
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() =>
                        navigate(`/ppe/routing/view/${r.id}`)
                      }
                    >
                      View
                    </button>

                    {r.approval_status === "DRAFT" && (
                      <>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() =>
                            navigate(`/ppe/routing/edit/${r.id}`)
                          }
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-outline-success"
                          onClick={() => submitRouting(r.id)}
                        >
                          Submit
                        </button>
                      </>
                    )}

                    {r.approval_status === "SUBMITTED" && (
                      <>
                        <button
                          className="btn btn-sm btn-outline-success"
                          onClick={() => approveRouting(r.id)}
                        >
                          Approve
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => rejectRouting(r.id)}
                        >
                          Reject
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
